import { Loader } from '../Widgets';
import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { Employee } from 'service/Models';
import ServerGateway from 'service/ServerGateway';
import Webcam from "react-webcam";
import toast, { Toaster } from 'react-hot-toast';

const LOCATION_CACHE_KEY = 'attendance_locations_cache';
const SELECTED_LOCATION_KEY = 'attendance_selected_location';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

interface CacheData {
    data: any[];
    timestamp: number;
}

export const PunchAttendance = () => {
    const user = useSelector((state: RootState) => state.appdata);
    const [employee, setEmployee] = useState<Employee>();
    const [locations, setLocations] = useState<any[]>([]);
    const [gatepasses, setGatepasses] = useState<any[]>([]);
    const [location, setLocation] = useState<any>();
    const [currentCoordinate, setCurrentCoordinate] = useState<any>();
    const [distance, setDistance] = useState<any>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isGatepassSaving, setIsGatepassSaving] = useState<boolean>(false);
    const [captureVideo, setCaptureVideo] = useState(false);
    const [isWithinGeofence, setIsWithinGeofence] = useState<boolean>(false);
    const [isRefetching, setIsRefetching] = useState<boolean>(false);

    const webcamRef = useRef(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    let geoLocationOptions = {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 0,
    };

    useEffect(() => {
        if (user.profile) {
            setEmployee(user.profile.profile);
        }
    }, [user]);

    useEffect(() => {
        if (employee) {
            getGatepasses();
            getLocations();
        }
    }, [employee]);

    useEffect(() => {
        navigator.geolocation.getCurrentPosition(function (position) {
            setCurrentCoordinate(position.coords);
        });

        navigator.geolocation.watchPosition(onChangePosition, null, geoLocationOptions);
    }, []);

    const onChangePosition = (position) => {
        setCurrentCoordinate(position.coords);
    }

    useEffect(() => {
        if (location && currentCoordinate) {
            let distance = calculateDistance(location.lattitude, location.longitude, currentCoordinate.latitude, currentCoordinate.longitude).toFixed(2);
            setDistance(distance);
            let isWithinGeofence = distance <= location.radius;
            setIsWithinGeofence(isWithinGeofence);
        }
        else {
            setIsWithinGeofence(false);
        }
    }, [location, currentCoordinate]);

    const handleVideoOnPlay = () => {
        setCaptureVideo(true);
    }

    const getGatepasses = () => {
        ServerGateway
            .getGatepass(employee?.id, "0")
            .then((data) => {
                setGatepasses(data.entries);
            })
            .catch(error => {
                console.log(error);
            });
    }

    function calculateDistance(lat1, lon1, lat2, lon2) {
        if ((lat1 == lat2) && (lon1 == lon2)) {
            return 0;
        }
        else {
            var radlat1 = Math.PI * lat1 / 180;
            var radlat2 = Math.PI * lat2 / 180;
            var theta = lon1 - lon2;
            var radtheta = Math.PI * theta / 180;
            var dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
            if (dist > 1) {
                dist = 1;
            }
            dist = Math.acos(dist);
            dist = dist * 180 / Math.PI;
            dist = dist * 60 * 1.1515;
            dist = dist * 1.609344;
            return dist * 1000;
        }
    }

    // Cache management functions
    const getCachedLocations = (): CacheData | null => {
        try {
            const cached = localStorage.getItem(LOCATION_CACHE_KEY);
            if (cached) {
                const cacheData: CacheData = JSON.parse(cached);
                const now = new Date().getTime();
                if (now - cacheData.timestamp < CACHE_DURATION) {
                    return cacheData;
                }
            }
        } catch (error) {
            console.log('Error reading cache:', error);
        }
        return null;
    };

    const setCachedLocations = (data: any[]) => {
        try {
            const cacheData: CacheData = {
                data: data,
                timestamp: new Date().getTime()
            };
            localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(cacheData));
        } catch (error) {
            console.log('Error setting cache:', error);
        }
    };

    const clearLocationCache = () => {
        try {
            localStorage.removeItem(LOCATION_CACHE_KEY);
        } catch (error) {
            console.log('Error clearing cache:', error);
        }
    };

    const saveSelectedLocation = (locationId: string | number) => {
        try {
            localStorage.setItem(SELECTED_LOCATION_KEY, locationId.toString());
        } catch (error) {
            console.log('Error saving selected location:', error);
        }
    };

    const getSelectedLocationId = (): string | null => {
        try {
            return localStorage.getItem(SELECTED_LOCATION_KEY);
        } catch (error) {
            console.log('Error getting selected location:', error);
        }
        return null;
    };

    const punchAttendance = () => {
        if (!isWithinGeofence) {
            toast("You are out of the geofence boundry, you cannot punch attendance", { position: 'bottom-center', className: 'btn bg-danger' });
            return;
        }

        if (gatepasses.length > 0) {
            let lastGatepassTime = moment(gatepasses[0].issuedAt);
            let currentTime = moment();
            let diff = currentTime.diff(lastGatepassTime, 'minutes');
            if (diff < 5) {
                toast('Please wait for 5 minute before punching again', { position: 'bottom-center', className: 'btn bg-danger' });
                return;
            }
        }

        let remarks: any = {};
        if (locations && locations.length > 0) {
            remarks = {
                lat: currentCoordinate.latitude,
                long: currentCoordinate.longitude,
                location: location.name,
                distance: distance + "m"
            };
        }
        else {
            remarks = {
                lat: currentCoordinate.latitude,
                long: currentCoordinate.longitude
            };
        }
        const imageSrc = (webcamRef.current as any).getScreenshot();
        remarks.imageSrc = imageSrc;

        let gatepass = {
            gatepassType: 'App',
            employeeId: employee?.id,
            issuedAt: moment().format("YYYY-MM-DDTHH:mm:ss"),
            remarks: JSON.stringify(remarks)
        };
        setIsGatepassSaving(true);
        ServerGateway
            .postGatepass(gatepass)
            .then((data) => {
                setIsGatepassSaving(false);
                toast('Your punch information saved successfully', { position: 'bottom-center', className: 'btn bg-success' });
                getGatepasses();
            })
            .catch(error => {
                console.log(error);
                setIsGatepassSaving(false);
            });
    }

    const handleLocationChange = (event: any) => {
        const selectedId = event.target.value;
        const selectedLoc = locations.find(loc => loc.id.toString() === selectedId);
        if (selectedLoc) {
            setLocation(selectedLoc);
            saveSelectedLocation(selectedId);
        }
    };

    const getLocations = (forceRefresh = false) => {
        if (!employee) return;

        // Check cache first if not forcing refresh
        if (!forceRefresh) {
            const cached = getCachedLocations();
            if (cached) {
                setLocations(cached.data);
                const selectedId = getSelectedLocationId();
                if (selectedId) {
                    const selectedLoc = cached.data.find(loc => loc.id.toString() === selectedId);
                    if (selectedLoc) {
                        setLocation(selectedLoc);
                        setIsLoading(false);
                        return;
                    }
                }
                if (cached.data.length > 0) {
                    setLocation(cached.data[0]);
                    saveSelectedLocation(cached.data[0].id);
                }
                setIsLoading(false);
                return;
            }
        }

        setIsLoading(true);
        const locationRequest = employee.canLoginFromAnyLocation == true
            ? ServerGateway.getLocations()
            : ServerGateway.getLocations(employee.id);

        locationRequest
            .then((data) => {
                data = data.filter(loc => loc.isActive);
                setLocations(data);
                setCachedLocations(data);

                const selectedId = getSelectedLocationId();
                if (selectedId) {
                    const selectedLoc = data.find(loc => loc.id.toString() === selectedId);
                    if (selectedLoc) {
                        setLocation(selectedLoc);
                    } else if (data.length > 0) {
                        setLocation(data[0]);
                        saveSelectedLocation(data[0].id);
                    }
                } else if (data.length > 0) {
                    setLocation(data[0]);
                    saveSelectedLocation(data[0].id);
                }
                setIsLoading(false);
                setIsRefetching(false);
            })
            .catch(error => {
                console.log(error);
                setIsLoading(false);
                setIsRefetching(false);
            });
    }

    const handleRefreshLocations = () => {
        setIsRefetching(true);
        clearLocationCache();
        getLocations(true);
    };

    const formatDistance = (valueInMeters: number) => {
        if (!Number.isFinite(valueInMeters)) return '--';
        if (valueInMeters >= 1000) {
            return `${(valueInMeters / 1000).toFixed(2)} km`;
        }
        return `${valueInMeters.toFixed(2)} m`;
    };

    const numericDistance = Number(distance);
    const distanceLabel = formatDistance(numericDistance);
    const allowedRadius = Number(location?.radius || 0);
    const extraDistance = Number.isFinite(numericDistance)
        ? Math.max(numericDistance - allowedRadius, 0)
        : 0;
    const extraDistanceLabel = formatDistance(extraDistance);

    return (
        <div className="page-mark-attendance">
            <div className='container'>
                {isLoading &&
                    <Loader />
                }
                {!isLoading &&
                    <>
                        <div><Toaster /></div>
                        <div className='row' style={{ marginTop: "10px" }}>
                            <div className='card' style={{ padding: "10px" }}>
                                <>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                                        <h5 style={{ margin: 0 }}>Select Location</h5>
                                        <button 
                                            className="btn btn-sm btn-outline-primary" 
                                            onClick={handleRefreshLocations}
                                            disabled={isRefetching}
                                            style={{ padding: "5px 10px" }}
                                        >
                                            {isRefetching ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm text-primary" role="status" style={{ height: "0.9rem", width: "0.9rem", marginRight: "5px" }}>
                                                        <span className="sr-only">Refreshing...</span>
                                                    </span>
                                                    Refreshing...
                                                </>
                                            ) : (
                                                "Refresh"
                                            )}
                                        </button>
                                    </div>
                                    {locations && locations.length > 0 ? (
                                        <select 
                                            className="form-select" 
                                            value={location?.id || ""} 
                                            onChange={handleLocationChange}
                                            style={{ marginBottom: "10px" }}
                                        >
                                            {locations.map((loc) => (
                                                <option key={loc.id} value={loc.id}>
                                                    {loc.name}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <p className="text-muted">No locations available</p>
                                    )}
                                    <div style={{ marginTop: "10px", fontSize: "0.9em", color: "#666" }}>
                                        {location && (
                                            <>
                                                {isWithinGeofence && (
                                                    <div style={{ marginBottom: "8px" }}>
                                                        <span className="badge bg-success">Within Radius</span>
                                                    </div>
                                                )}
                                                {!isWithinGeofence && (
                                                    <>
                                                        <div className='alert alert-warning' style={{ marginBottom: "12px" }}>
                                                            <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                                                                You are outside the selected location radius.
                                                            </div>
                                                            <div>
                                                                Move closer to this location to enable the login button.
                                                            </div>
                                                        </div>
                                                        <div className="row" style={{ margin: 0 }}>
                                                            <div className="col-12 col-md-6" style={{ paddingLeft: 0, paddingRight: "10px", marginBottom: "10px" }}>
                                                                <div className="border rounded" style={{ padding: "10px", backgroundColor: "#f8f9fa", height: "100%" }}>
                                                                    <div className="text-muted" style={{ fontSize: "0.8rem", marginBottom: "4px" }}>Selected location</div>
                                                                    <div style={{ fontWeight: 600 }}>{location.name}</div>
                                                                    <div style={{ marginTop: "8px" }}>
                                                                        <span className='badge bg-warning text-dark'>Outside Radius</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="col-12 col-md-6" style={{ paddingLeft: 0, paddingRight: 0, marginBottom: "10px" }}>
                                                                <div className="border rounded" style={{ padding: "10px", backgroundColor: "#f8f9fa", height: "100%" }}>
                                                                    <div className="text-muted" style={{ fontSize: "0.8rem", marginBottom: "4px" }}>Distance from location</div>
                                                                    <div style={{ fontWeight: 600 }}>{distanceLabel}</div>
                                                                    <div style={{ marginTop: "6px" }}>You are {extraDistanceLabel} outside the allowed radius.</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="border rounded" style={{ padding: "10px", backgroundColor: "#f8f9fa" }}>
                                                            <div className="text-muted" style={{ fontSize: "0.8rem", marginBottom: "4px" }}>Current coordinates</div>
                                                            <div>{currentCoordinate?.latitude}:{currentCoordinate?.longitude}</div>
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </>
                            </div>
                            {isWithinGeofence &&
                                <>
                                    <div style={{ width: "100%", margin: "20px 0", padding: 0, position: 'relative' }}>
                                        <>
                                            <Webcam ref={webcamRef} style={{ width: "90vw", margin: "auto", maxWidth: "450px" }} onPlay={handleVideoOnPlay} />
                                        </>
                                    </div>
                                    {(captureVideo) &&
                                        <>
                                            <div className="btn bg-primary w-100" onClick={punchAttendance}>
                                                {isGatepassSaving &&
                                                    <div className="spinner-border text-light" role="status" style={{ height: "1.2rem", width: "1.2rem" }}>
                                                        <span className="sr-only">Saving...</span>
                                                    </div>
                                                }
                                                {!isGatepassSaving &&
                                                    <>
                                                        {gatepasses.length == 0 && <span>Punch In</span>}
                                                        {gatepasses.length > 0 && (gatepasses.length % 2) == 0 && <span>Punch In</span>}
                                                        {gatepasses.length > 0 && (gatepasses.length % 2) == 1 && <span>Punch Out</span>}
                                                    </>
                                                }
                                            </div>
                                        </>
                                    }
                                </>
                            }
                        </div>
                        {isWithinGeofence &&
                            <div className='row' style={{ marginTop: "10px" }}>
                                <>
                                    <ul className="list-group list-group-flush">
                                        {gatepasses && gatepasses.map((g) => (
                                            <li className="list-group-item">
                                                {moment(g.issuedAt).format("hh:mm a")}
                                                <div className={g.isLogin ? 'badge bg-success' : 'badge bg-danger'} style={{ float: "right" }}>
                                                    {g.isLogin ? "IN" : "OUT"}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            </div>
                        }
                    </>
                }
            </div>
        </div >
    );
}